import React from "react";
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonButton,
  IonTitle,
  IonContent,
  IonItem,
  IonSearchbar,
  IonLabel,
  IonText,
  IonCheckbox,
  IonList,
  IonAvatar,
  IonLoading,
} from "@ionic/react";

type AssociateUser = {
  WerkerUid: string;
  WerkerFirstName: string;
  WerkerLastName: string;
  WerkerProfilePicURL: string;
  WerkerCompany: string;
  WerkerBio: string;
};

type Props = {
  isOpen: boolean;
  onDidDismiss: () => void;

  onSave: () => void;
  savingMembers: boolean;

  memberSearchText: string;
  setMemberSearchText: (v: string) => void;

  memberSearchLower: string;

  toggleSelectAllVisible: () => void;

  allVisibleSelected: boolean;
  someVisibleSelected: boolean;

  visibleSelectedCount: number;
  visibleUidsCount: number;

  filteredSortedMembers: AssociateUser[];

  selectedMemberIds: string[];
  toggleMember: (uid: string, checked: boolean) => void;
};

export const EditCrewMembersModal: React.FC<Props> = ({
  isOpen,
  onDidDismiss,
  onSave,
  savingMembers,
  memberSearchText,
  setMemberSearchText,
  memberSearchLower,
  toggleSelectAllVisible,
  allVisibleSelected,
  someVisibleSelected,
  visibleSelectedCount,
  visibleUidsCount,
  filteredSortedMembers,
  selectedMemberIds,
  toggleMember,
}) => {
  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onDidDismiss}
    >
      <IonHeader>
        <IonToolbar
          color="secondWarning"
        >
          <IonButtons
            slot="start"
          >
            <IonButton
              onClick={onDidDismiss}
            >
              Close
            </IonButton>
          </IonButtons>

          <IonTitle>
            Edit Crew Members
          </IonTitle>

          <IonButtons
            slot="end"
          >
            <IonButton
              onClick={onSave}
              disabled={savingMembers}
            >
              Save
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {/* Modal-only filter */}
        <IonItem
          style={{ marginTop: "12px" }}
          lines="full"
        >
          <IonSearchbar
            value={memberSearchText}
            onIonChange={(e) => setMemberSearchText(e.detail.value || "")}
            animated
            placeholder="Filter by name, company, bio..."
          />
        </IonItem>

        {/* Select All row */}
        <IonItem
          button={true}
          detail={false}
          onClick={toggleSelectAllVisible}
          lines="full"
        >
          <IonLabel>
            {memberSearchLower ? "Select All (filtered)" : "Select All"}
            <IonText
              color="medium"
            >
              <div style={{ marginTop: "2px", fontSize: "13px" }}>
                {visibleSelectedCount} Members / {visibleUidsCount} Associates
              </div>
            </IonText>
          </IonLabel>

          {/* Display-only checkbox (no handler, disabled) */}
          <IonCheckbox
            slot="end"
            checked={Boolean(allVisibleSelected)}
            indeterminate={Boolean(!allVisibleSelected && someVisibleSelected)}
            disabled={true}
          />
        </IonItem>

        {/* Members list */}
        <div>
          {filteredSortedMembers?.length ? (
            <IonList>
              {filteredSortedMembers.map((a: any) => {
                const uid = a?.WerkerUid || "";
                if (!uid) return null;

                const first = a?.WerkerFirstName || "";
                const last = a?.WerkerLastName || "";
                const name = `${first} ${last}`.trim() || "Associate";

                const isChecked = selectedMemberIds.includes(uid);

                return (
                  <IonItem
                    key={uid}
                    lines="full"
                  >
                    <IonAvatar
                      slot="start"
                    >
                      <img
                        src={a.WerkerProfilePicURL}
                        alt={name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </IonAvatar>

                    <IonLabel
                      className="ion-text-wrap"
                    >
                      <strong>{name}</strong>
                      {a?.WerkerCompany && (
                        <div style={{ marginTop: "2px" }}>
                          <IonText
                            color="medium"
                          >
                            {a.WerkerCompany}
                          </IonText>
                        </div>
                      )}
                    </IonLabel>

                    <IonCheckbox
                      slot="end"
                      checked={isChecked}
                      onIonChange={(e) => toggleMember(uid, !!e.detail.checked)}
                    />
                  </IonItem>
                );
              })}
            </IonList>
          ) : (
            <IonItem
              lines="none"
            >
              <IonLabel
                color="medium"
              >
                No associates yet.
              </IonLabel>
            </IonItem>
          )}
        </div>

        <IonLoading isOpen={savingMembers} message="Saving..." />
      </IonContent>
    </IonModal>
  );
};
